import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StompSubscription } from '@stomp/stompjs';
import { Literals } from 'src/app/literals';
import {
  FetchScheduleHistoryPayload,
  ScheduleHistoryListItem,
  ScheduleSuiteEntry,
  SchedulerExecutionHistoryService
} from 'src/app/Services/scheduler-execution-history.service';
import { WebsocketService } from 'src/app/Services/websocket.service';

type RunStatusKind = 'passed' | 'failed' | 'running' | 'not_started' | 'aborted' | 'aborting' | 'waiting';

@Component({
  selector: 'ze-scheduler-execution-history',
  templateUrl: './scheduler-execution-history.component.html',
  styleUrls: ['./scheduler-execution-history.component.scss']
})
export class SchedulerExecutionHistoryComponent implements OnInit {

  searchQuery = '';
  appliedScheduleName = '';

  history: ScheduleHistoryListItem[] = [];
  expandedLogId: string | null = null;

  /** Suite panel state for the currently expanded row. */
  suiteSearchQuery = '';
  appliedSuiteName = '';
  suiteEntries: ScheduleSuiteEntry[] = [];
  /** Mode of the expanded schedule run; older logs have none and are treated as parallel. */
  logExecutionMode = '';
  logContinueOnFailure = true;

  get isSequentialLog(): boolean {
    return this.logExecutionMode === 'SEQUENTIAL';
  }
  suiteCurrentPage = 1;
  readonly suitePageSize = 5;
  suiteTotalCount = 0;
  suiteStartIndex = 0;
  suiteHasNext = false;
  loadingSuites = false;
  suiteErrorMessage = '';

  currentPage = 1;
  readonly pageSize = 10;
  totalCount = 0;
  startIndex = 0;
  hasNext = false;

  loading = false;
  errorMessage = '';

  /** Whether the WebSocket connection is up. */
  private wsConnected = false;

  /** Subscription to execution update events for the currently expanded row. */
  private stompScheduledUpdateSub: StompSubscription | null = null;


  constructor(
    private schedulerExecutionHistoryService: SchedulerExecutionHistoryService,
    private route: ActivatedRoute,
    private router: Router,
    private websocketService: WebsocketService
  ) { }

  ngOnInit(): void {
    const fromRoute = this.route.snapshot.queryParamMap.get('scheduleName') || '';
    if (fromRoute) {
      this.searchQuery = fromRoute;
      this.appliedScheduleName = fromRoute;
    }
    
    // Establish WebSocket connection (mirrors report-view pattern).
    // loadHistory() is called after the connection is ready so that any
    // row the user immediately expands can subscribe without a race.
    this.websocketService._connect();

    this.websocketService.onConnection().subscribe(connected => {
      if (connected) {
        this.wsConnected = true;
        this.loadHistory();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeScheduledUpdate();
    // Only disconnect if this component owns the socket session.
    // If other components share the same WebsocketService singleton and
    // remain alive, remove the _disconnect() call.
    this.websocketService._disconnect();

  }

  onSearch(): void {
    this.appliedScheduleName = this.searchQuery.trim();
    this.currentPage = 1;
    this.collapseExpanded();
    this.loadHistory();
  }

  onSearchKeyup(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  private searchDebounceTimer: any = null;
  private suiteSearchDebounceTimer: any = null;

  onSearchInput(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.onSearch(), 400);
  }

  onSuiteSearchInput(): void {
    clearTimeout(this.suiteSearchDebounceTimer);
    this.suiteSearchDebounceTimer = setTimeout(() => this.onSuiteSearch(), 400);
  }

  get showClearSearch(): boolean {
    return !!(this.searchQuery && this.searchQuery.trim()) || !!this.appliedScheduleName;
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.appliedScheduleName = '';
    this.currentPage = 1;
    this.collapseExpanded();
    this.loadHistory();
  }

  onSuiteSearch(): void {
    this.appliedSuiteName = this.suiteSearchQuery.trim();
    this.suiteCurrentPage = 1;
    this.loadExpandedSuites();
  }

  onSuiteSearchKeyup(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSuiteSearch();
    }
  }

  get showClearSuiteSearch(): boolean {
    return !!(this.suiteSearchQuery && this.suiteSearchQuery.trim()) || !!this.appliedSuiteName;
  }

  clearSuiteSearch(): void {
    this.suiteSearchQuery = '';
    this.appliedSuiteName = '';
    this.suiteCurrentPage = 1;
    this.loadExpandedSuites();
  }

  get totalPages(): number {
    if (!this.totalCount || !this.pageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get suiteTotalPages(): number {
    if (!this.suiteTotalCount || !this.suitePageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.suiteTotalCount / this.suitePageSize));
  }

  get rangeStart(): number {
    if (!this.history.length) {
      return 0;
    }
    return this.startIndex + 1;
  }

  get rangeEnd(): number {
    if (!this.history.length) {
      return 0;
    }
    return Math.min(this.startIndex + this.history.length, this.totalCount);
  }

  get suiteRangeStart(): number {
    if (!this.suiteEntries.length) {
      return 0;
    }
    return this.suiteStartIndex + 1;
  }

  get suiteRangeEnd(): number {
    if (!this.suiteEntries.length) {
      return 0;
    }
    return Math.min(this.suiteStartIndex + this.suiteEntries.length, this.suiteTotalCount);
  }

  reload(): void {
    this.loadHistory();
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.hasNext) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.collapseExpanded();
    this.loadHistory();
  }

  goToSuitePreviousPage(): void {
    if (this.suiteCurrentPage > 1) {
      this.goToSuitePage(this.suiteCurrentPage - 1);
    }
  }

  goToSuiteNextPage(): void {
    if (this.suiteHasNext) {
      this.goToSuitePage(this.suiteCurrentPage + 1);
    }
  }

  goToSuitePage(page: number): void {
    if (page < 1 || page > this.suiteTotalPages || page === this.suiteCurrentPage) {
      return;
    }
    this.suiteCurrentPage = page;
    this.loadExpandedSuites();
  }

  toggleRow(row: ScheduleHistoryListItem): void {
    const logId = row && row._id && row._id.$oid;
    if (!logId) {
      return;
    }

    if (this.expandedLogId === logId) {
      this.collapseExpanded();
      return;
    }

    this.expandedLogId = logId;
    this.resetSuitePanel();
    this.loadExpandedSuites();
  }

  isExpanded(row: ScheduleHistoryListItem): boolean {
    const logId = row && row._id && row._id.$oid;
    return !!logId && this.expandedLogId === logId;
  }

  viewReport(entry: ScheduleSuiteEntry, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const executionId = entry && entry.execution_id && entry.execution_id.$oid;
    if (!executionId) {
      return;
    }
    // Open report view in the "schedule" tab.
    this.router.navigate(['/report'], { queryParams: { openTab: Literals.SCHEDULE, reportId: executionId } });
  }

  trackByHistoryId(_index: number, item: ScheduleHistoryListItem): string {
    return (item && item._id && item._id.$oid) ? item._id.$oid : String(_index);
  }

  trackBySuiteEntryIndex(_index: number): number {
    return _index;
  }

  getSuiteCode(entry: ScheduleSuiteEntry): string {
    if (entry && entry.suite_name) {
      return String(entry.suite_name).trim().toUpperCase().replace(/\s+/g, '_');
    }
    const id = entry && entry.suite_id && entry.suite_id.$oid;
    if (id) {
      return id.slice(-12).toUpperCase();
    }
    return 'SUITE';
  }

  formatBrowser(browser: string): string {
    if (!browser) {
      return '';
    }
    const b = String(browser).trim();
    return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
  }

  getSuiteRunKind(entry: ScheduleSuiteEntry): RunStatusKind {

    // No execution_id means dispatch never produced a run
    if (!entry || !(entry.execution_id && entry.execution_id.$oid)) {
      return 'not_started';
    }

    const dispatch = entry && entry.dispatch_status
      ? String(entry.dispatch_status).trim().toUpperCase()
      : '';
    if (dispatch === 'FAILED') {
      return 'failed';
    }

    const text = entry && entry.run_status_text ? String(entry.run_status_text).trim() : '';
    if (text === 'Completed') {
      return 'passed';
    }
    if (text === 'Execution Failed') {
      return 'failed';
    }
    if (text === 'Aborted') {
    return 'aborted';
    }
    if (text === 'Aborting') {
      return 'aborting';
    }
    // Sequential run: a later suite is created up front but has not been started by Helios yet.
    if (this.isSequentialLog && !text && entry.run_status !== undefined && entry.run_status <= 1) {
      return 'waiting';
    }
    return 'running';
  }

  getSuiteStatusLabel(entry: ScheduleSuiteEntry): string {
    const kind = this.getSuiteRunKind(entry);
    if (kind === 'passed') {
      return 'Passed';
    }
    if (kind === 'failed') {
      return 'Failed';
    }

    if (kind === 'aborted') {
      return 'Aborted';
    }
    if (kind === 'aborting') {
      return 'Aborting';
    }

    if (kind === 'not_started') {
      return 'Not Started';
    }
    if (kind === 'waiting') {
      return 'Waiting';
    }
    return 'Running';
  }

  suiteStatusClass(entry: ScheduleSuiteEntry): string {
    const kind = this.getSuiteRunKind(entry);
    if (kind === 'passed') {
      return 'suite-status--passed';
    }
    if (kind === 'failed') {
      return 'suite-status--failed';
    }
    if (kind === 'aborted') {
    return 'suite-status--aborted';
    }
    if (kind === 'aborting') {
      return 'suite-status--aborting';
    }
    if (kind === 'not_started' || kind === 'waiting') { 
      return 'suite-status--not-started';
    }
    return 'suite-status--running';
  }

  getScheduleRowStatus(row: ScheduleHistoryListItem): { label: string; cssClass: string; isRunning: boolean } {
    if (this.isExpanded(row) && this.suiteEntries.length) {
      return this.deriveStatusFromSuites(this.suiteEntries);
    }
    return this.deriveStatusFromBackend(row.status);
  }

   // ─── WebSocket: subscribe when a row is expanded ─────────────────────────
     /**
   * Subscribe to /message/db1/project/scheduled/exeupdate/{projectId}.
   * When a message arrives, find the matching suiteEntry by execution_id
   * and patch its run_status_text / dispatch_status in place.
   *
   * Always unsubscribes the previous subscription first (mirrors report-view).
   */
  private subscribeToScheduledExecutionUpdates(): void {
    const projectId = localStorage.getItem('projectId');
    if (!projectId || !this.wsConnected || !this.websocketService.stompClient) {
      return;
    }
        // Always tear down before re-subscribing.
    this.unsubscribeScheduledUpdate();

    const topic = `/message/db1/project/scheduled/exeupdate/${projectId}`;

    this.stompScheduledUpdateSub = this.websocketService.stompClient.subscribe(
      topic,
      (message) => {
  try {
    const body = JSON.parse(message.body);
    const updatedExecution = body && body.data ? body.data : null;
    if (!updatedExecution) { return; }

    const updatedId: string = (updatedExecution._id && updatedExecution._id.$oid)
      ? updatedExecution._id.$oid
      : (updatedExecution._id || '');

    if (!updatedId) { return; }

    const idx = this.suiteEntries.findIndex(function(entry) {
      const entryExeId: string = (entry.execution_id && entry.execution_id.$oid)
        ? entry.execution_id.$oid
        : ((entry.execution_id as any) || '');
      return entryExeId === updatedId;
    });

    if (idx === -1) { return; }

    this.suiteEntries[idx] = {
      ...this.suiteEntries[idx],
      run_status_text: updatedExecution.run_status_text !== undefined && updatedExecution.run_status_text !== null
        ? updatedExecution.run_status_text
        : this.suiteEntries[idx].run_status_text,
      dispatch_status: updatedExecution.dispatch_status !== undefined && updatedExecution.dispatch_status !== null
        ? updatedExecution.dispatch_status
        : this.suiteEntries[idx].dispatch_status,
    };

  } catch (e) {
          console.error('SchedulerExecutionHistory: error processing WS message', e);
        }
      }
    );
  }

    private unsubscribeScheduledUpdate(): void {
    if (this.stompScheduledUpdateSub) {
      this.stompScheduledUpdateSub.unsubscribe();
      this.stompScheduledUpdateSub = null;
    }
  }

    // ─── Suite loading ────────────────────────────────────────────────────────


  private deriveStatusFromSuites(entries: ScheduleSuiteEntry[]): { label: string; cssClass: string; isRunning: boolean } {
    let hasRunning = false;
    let hasFailed = false;
    let hasAborted = false;
    let allPassed = entries.length > 0;

    for (let i = 0; i < entries.length; i++) {
      const kind = this.getSuiteRunKind(entries[i]);
      if (kind === 'running') {
        hasRunning = true;
        allPassed = false;
      } else if (kind === 'failed') {
        hasFailed = true;
        allPassed = false;
      } else if (kind === 'aborted') {
      hasAborted = true;
      allPassed = false;
      } else if (kind === 'not_started') {
        allPassed = false;          // not_started doesn't count as passed
        // but also doesn't force "Running" — only actual running suites do
      } else if (kind !== 'passed') {
        allPassed = false;
      }
    }

    if (hasRunning) {
      return { label: 'Running', cssClass: 'status-pill--running', isRunning: true };
    }
    if (hasFailed) {
      return { label: 'Failed', cssClass: 'status-pill--failed', isRunning: false };
    }
    if (hasAborted) {
      return { label: 'Aborted', cssClass: 'status-pill--aborted', isRunning: false };
    }
    if (allPassed) {
      return { label: 'Completed', cssClass: 'status-pill--completed', isRunning: false };
    }
    return { label: 'Running', cssClass: 'status-pill--running', isRunning: true };
  }

  private deriveStatusFromBackend(status?: string): { label: string; cssClass: string; isRunning: boolean } {
    const s = status ? String(status).trim().toUpperCase() : '';
    if (s === 'FAILED') {
      return { label: 'Failed', cssClass: 'status-pill--failed', isRunning: false };
    }
    if (s === 'RUNNING' || s === 'INPROGRESS' || s === 'IN_PROGRESS') {
      return { label: 'Running', cssClass: 'status-pill--running', isRunning: true };
    }
    if (s === 'ABORTING') {
    return { label: 'Aborting', cssClass: 'status-pill--aborting', isRunning: true };
    }
    if (s === 'ABORTED') {
      return { label: 'Aborted', cssClass: 'status-pill--aborted', isRunning: false };
    }
    if (s === 'SKIPPED') {
      return { label: 'Skipped', cssClass: 'status-pill--skipped', isRunning: false };
    }
    if (s === 'ALLDISPATCHED' || s === 'COMPLETED') {
      return { label: 'Completed', cssClass: 'status-pill--completed', isRunning: false };
    }
    return { label: status || 'Unknown', cssClass: 'status-pill--completed', isRunning: false };
  }

 private collapseExpanded(): void {
  this.expandedLogId = null;
  this.unsubscribeScheduledUpdate();  // add this line
  this.resetSuitePanel();
}
  private resetSuitePanel(): void {
    this.suiteSearchQuery = '';
    this.appliedSuiteName = '';
    this.suiteEntries = [];
    this.suiteCurrentPage = 1;
    this.suiteTotalCount = 0;
    this.suiteStartIndex = 0;
    this.suiteHasNext = false;
    this.loadingSuites = false;
    this.suiteErrorMessage = '';
  }

  retryLoadSuites(): void {
    this.loadExpandedSuites();
  }

  private loadExpandedSuites(): void {
    if (!this.expandedLogId) {
      return;
    }

    this.loadingSuites = true;
    this.suiteErrorMessage = '';

    const payload: any = {
      logId: this.expandedLogId,
      page: String(this.suiteCurrentPage),
      maxCount: String(this.suitePageSize)
    };

    const suiteName = this.appliedSuiteName.trim();
    if (suiteName) {
      payload.suiteName = suiteName;
    }

    this.schedulerExecutionHistoryService.fetchScheduleLogSuites(payload).subscribe({
      next: resp => {
        this.loadingSuites = false;
        if (!resp || resp.operationStatus !== 'SUCCESS') {
          this.suiteErrorMessage = (resp && resp.operationMessage) || 'Failed to load suite entries.';
          this.suiteEntries = [];
          this.suiteTotalCount = 0;
          this.suiteHasNext = false;
          return;
        }

        const pageData = resp.data || ({} as any);
const logItem = (pageData.data && pageData.data.length) ? pageData.data[0] : null;
this.suiteEntries = (logItem && logItem.suite_entries) ? logItem.suite_entries : [];
this.logExecutionMode = (logItem && logItem.execution_mode) ? logItem.execution_mode : '';
this.logContinueOnFailure = !(logItem && logItem.continue_on_failure === false);
this.suiteTotalCount = pageData.totalCount || 0;
this.suiteStartIndex = pageData.startIndex || 0;
this.suiteHasNext = !!pageData.hasNext;

        // Now that we have entries, start (or restart) the WS subscription
        // so live status updates flow in for the visible execution_ids.
        if (this.suiteEntries.length) {
          this.subscribeToScheduledExecutionUpdates();
        }
      },
      error: () => {
        this.loadingSuites = false;
        this.suiteErrorMessage = 'Unable to load suite entries. Please try again.';
        this.suiteEntries = [];
        this.suiteTotalCount = 0;
        this.suiteHasNext = false;
      }
    });
  }

  private loadHistory(): void {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.errorMessage = 'No project selected. Open a project to view schedule history.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const payload = this.buildFetchPayload(projectId);

    this.schedulerExecutionHistoryService.fetchScheduleHistory(payload).subscribe({
      next: resp => {
        this.loading = false;
        if (!resp || resp.operationStatus !== 'SUCCESS') {
          this.errorMessage = (resp && resp.operationMessage) || 'Failed to load schedule history.';
          this.history = [];
          this.totalCount = 0;
          this.hasNext = false;
          return;
        }

        const pageData = resp.data || ({} as any);
        this.history = pageData.data || [];
        this.totalCount = pageData.totalCount || 0;
        this.startIndex = pageData.startIndex || 0;
        this.hasNext = !!pageData.hasNext;
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Unable to load schedule history. Please try again.';
        this.history = [];
        this.totalCount = 0;
        this.hasNext = false;
      }
    });
  }

  private buildFetchPayload(projectId: string): FetchScheduleHistoryPayload {
    const payload: FetchScheduleHistoryPayload = {
      projectId,
      page: String(this.currentPage),
      maxCount: String(this.pageSize)
    };

    const name = this.appliedScheduleName.trim();
    if (name) {
      payload.scheduleName = name;
    }

    return payload;
  }
}
